import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CryptoJS from 'crypto-js'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { loginErrorIntervalMinutes, loginErrorLimitTimes } from '../constants/loginLimit'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC003AuthMfaCodeCreate } from '../hooks/useC003AuthMfaCodeCreate'
import { useC101UserGet } from '../hooks/useC101UserGet'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import { loginIdSchema, softPasswordSchema } from '../schema/loginSchema'
import { LanguageContext } from '../util/LanguageContext'
import { VersionContext } from '../util/VersionContext'

// ログイン画面
export const Login = () => {
  // auth
  const auth = useAuth()
  const c101 = useC101UserGet()
  const c003 = useC003AuthMfaCodeCreate()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // バージョン取得用
  const { version } = useContext(VersionContext)

  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  const [checked, setChecked] = useState(false)
  const [loading, setLoading] = useState(loadStatus.NONE)
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [errorLoginId, setErrorLoginId] = useState('')
  const [errorPassword, setErrorPassword] = useState('')

  // company判定用
  const uri = new URL(window.location.href)
  const getCompany = () => {
    const hostname = uri.hostname
    if (
      process.env.REACT_APP_DOMAIN_TA &&
      hostname.indexOf(process.env.REACT_APP_DOMAIN_TA) !== -1
    ) {
      return 'TA'
    }
    return 'KC'
  }
  const company = getCompany()

  const validateLoginId = async (value: string) => {
    try {
      await loginIdSchema.validate(value)
      setErrorLoginId('')
      if (password !== '' && errorPassword === '') setIsDisabled(false)
    } catch (err: any) {
      setErrorLoginId(err.message)
      setIsDisabled(true)
    }
  }

  const validatePassword = async (value: string) => {
    try {
      await softPasswordSchema.validate(value)
      setErrorPassword('')
      if (loginId !== '' && errorLoginId === '') setIsDisabled(false)
    } catch (err: any) {
      setErrorPassword(err.message)
      setIsDisabled(true)
    }
  }
  const navigate = useNavigate()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) {
      //ローカルストレージ削除
      localStorage.removeItem('loginId')
      localStorage.removeItem('password')
      // setIsDisabled(true);
    }
    setChecked(event.target.checked)
  }
  // 起動時設定
  useEffect(() => {
    const savedLoginId = localStorage.getItem('loginId')
    const encrypted = localStorage.getItem('password')
    const savedPassword =
      encrypted && CryptoJS.AES.decrypt(encrypted, 'Cotopat').toString(CryptoJS.enc.Utf8)

    if (savedLoginId && savedPassword) {
      setLoginId(savedLoginId)
      setPassword(savedPassword)

      setChecked(true)
      setIsDisabled(false)
    }
    setIsDisabled(!(savedLoginId && savedPassword))
  }, [])

  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
    setLoading(loadStatus.NONE)
  }

  const handleLoginidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(event.target.value)
    validateLoginId(event.target.value)
    // setIsDisabled(event.target.value === "" || password === "");
  }

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    validatePassword(event.target.value)
    // setIsDisabled(event.target.value === "" || loginId === "");
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  const onClickLogin = async () => {
    setLoading(loadStatus.LOADING)
    setIsDisabled(true)

    // ログイン失敗回数変数
    const loginIdErrCntName = loginId + 'ErrCnt'
    // ログイン失敗時刻変数
    const loginIdErrTimeName = loginId + 'ErrTime'
    // ローカルストレージからログイン失敗回数とログイン失敗時刻を取得する
    const loginIdErrCnt = localStorage.getItem(loginIdErrCntName)
    const loginIdErrTime = localStorage.getItem(loginIdErrTimeName)
    let errorCount = 0
    const currentTime: number = Date.now()

    if (loginIdErrCnt) {
      errorCount = Number.parseInt(loginIdErrCnt, 10)
    }
    if (loginIdErrTime) {
      const savedTime: number = Number.parseInt(loginIdErrTime, 10)
      const thresholdTime: number = savedTime + loginErrorIntervalMinutes * 60 * 1000
      // console.log(`currentTime:${currentTime} thresholdTime:${thresholdTime} `);
      if (currentTime > thresholdTime) {
        // console.log("一定時間経過している場合ログイン失敗回数を0クリア");
        errorCount = 0
      }
    }
    // ログイン失敗回数を超過している場合
    if (errorCount >= loginErrorLimitTimes) {
      console.log(`ログイン失敗回数超過：${errorCount}`)
      setErrorDialog(errorMsg.AUTH_LIMIT_ERROR)
      setOpenDialog(true)
      setIsDisabled(false)
      return
    }

    // サインイン
    const result = await auth.signIn(loginId, password)
    if (result.success) {
      // ログイン成功時は、ログイン制限チェック関数があれば削除する
      if (loginIdErrCnt) {
        localStorage.removeItem(loginIdErrCntName)
      }
      if (loginIdErrTime) {
        localStorage.removeItem(loginIdErrTimeName)
      }
      if (checked) {
        // ログイン情報を保存
        localStorage.setItem('loginId', loginId)
        const encrypted = CryptoJS.AES.encrypt(password, 'Cotopat').toString()
        localStorage.setItem('password', encrypted)
      }
      // ユーザー取得
      const resultUser = await c101.execUserGet(loginId, result.message!)
      if (resultUser.success && resultUser.info) {
        const userInfo = resultUser.info
        auth.setDeviceId(userInfo.device_id ?? '')
        auth.setRole(userInfo.role)
        auth.setRhq(userInfo.rhq)
        auth.setSalesCompany(userInfo.sales_company ?? '')

        if (userInfo.authenticated === false) {
          // メールアドレス登録画面
          navigate('/emailentry')
        } else {
          // root権限は2段階認証を行わない
          if (userInfo.role === 0) {
            navigate('/user')
            // 4:京セラ(KDC)は2段階認証を行わない
          } else if (userInfo.role === 4) {
            navigate('/device')
          } else {
            // 2段階認証画面
            const resultMfa = await c003.execAuthMfaCodeCreate(loginId)
            if (resultMfa.success) {
              navigate('/mfa')
            } else {
              setErrorDialog(resultMfa.message!)
              setOpenDialog(true)
            }
          }
        }
      } else {
        setErrorDialog(resultUser.message!)
        setOpenDialog(true)
      }
    } else {
      // ログイン失敗ルート
      errorCount++
      // ログイン失敗回数と失敗時刻を書込み
      localStorage.setItem(loginIdErrCntName, errorCount.toString())
      localStorage.setItem(loginIdErrTimeName, currentTime.toString())

      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
    setIsDisabled(false)
  }

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let kyoceraJsx: JSX.Element
  switch (baseLanguage) {
    case 'ja':
      kyoceraJsx = (
        <Typography
          sx={{
            width: '320px',
            height: '24px',
            textAlign: 'center',
            letterSpacing: '0px',
            color: '#282828',
          }}
        >
          <Link
            href="/passwordreset"
            onClick={(e: any) => {
              e.preventDefault()
              // メールアドレス登録画面
              navigate('/passwordreset')
            }}
            color="inherit"
            sx={{ font: 'normal normal bold 16px/24px Noto Sans JP' }}
          >
            {translation['link_forgot_credentials']}
          </Link>
        </Typography>
      )
      break
    default:
      kyoceraJsx = (
        <Typography
          sx={{
            width: '320px',
            height: '24px',
            textAlign: 'center',
            letterSpacing: '0px',
            color: '#282828',
            mb: '30px',
          }}
        >
          <Link
            href="/passwordreset"
            onClick={(e: any) => {
              e.preventDefault()
              // メールアドレス登録画面
              navigate('/passwordreset')
            }}
            color="inherit"
            sx={{ font: 'normal normal bold 16px/24px Noto Sans JP' }}
          >
            {translation['link_forgot_credentials']}
          </Link>
        </Typography>
      )
      break
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'auto',
          marginTop: '80px',
        }}
      >
        <Paper
          elevation={5}
          sx={{
            width: '787px',
            boxShadow: '0px 2px 2px #00000026',
            borderRadius: '10px',
          }}
        >
          <Box
            display="flex"
            position="relative"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Stack direction="column">
              <Box mt="111px" display="flex" justifyContent="center">
                <CotopatImage width="172px" height="44px" />
              </Box>
              <Typography
                sx={{
                  mt: '80px',
                  top: '315px',
                  left: '759px',
                  font: 'normal normal normal 21px/30px Noto Sans JP',
                }}
              >
                {translation['label_id']}
              </Typography>
              <TextField
                value={loginId}
                error={!!errorLoginId}
                helperText={errorLoginId ? translation[errorLoginId] : ' '}
                onChange={handleLoginidChange}
                InputProps={{ sx: { background: '#F4F4F4' } }}
                sx={{
                  mt: '8px',
                  width: '402px',
                  borderRadius: '10px',
                  '& .MuiFormHelperText-root': {
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiOutlinedInput-root': {
                    // "& fieldset": {
                    //   borderColor: "white",
                    // },
                    // "&:hover fieldset": {
                    //   borderColor: "white",
                    // },
                    '&.Mui-focused fieldset': {
                      borderColor: errorLoginId ? 'red' : '#282828',
                    },
                  },
                  '& .MuiInputBase-input.MuiOutlinedInput-input': {
                    padding: '15px 14px',
                  },
                }}
              />
              <Typography
                sx={{
                  mt: '25.1px',
                  top: '315px',
                  left: '759px',
                  font: 'normal normal normal 21px/30px Noto Sans JP',
                }}
              >
                {translation['label_password']}
              </Typography>
              <TextField
                type={showPassword ? 'text' : 'password'}
                value={password}
                error={!!errorPassword}
                helperText={errorPassword ? translation[errorPassword] : ' '}
                onChange={handlePasswordChange}
                InputProps={{
                  endAdornment: password ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePasswordVisibility}>
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: {
                    background: '#F4F4F4',
                  },
                }}
                sx={{
                  mt: '8px',
                  width: '402px',
                  borderRadius: '10px',
                  'input::-ms-reveal': {
                    display: 'none' /* -ms-revealアイコンを非表示にする */,
                  },
                  '& .MuiFormHelperText-root': {
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiOutlinedInput-root': {
                    // "& fieldset": {
                    //   borderColor: "white",
                    // },
                    // "&:hover fieldset": {
                    //   borderColor: "white",
                    // },
                    '&.Mui-focused fieldset': {
                      borderColor: errorPassword ? 'red' : '#282828',
                    },
                  },
                  '& .MuiInputBase-input.MuiOutlinedInput-input': {
                    padding: '15px 14px',
                  },
                }}
                variant="outlined"
              />
              <Box display="flex" justifyContent="center" alignItems="center" mt="48.1px">
                <Checkbox
                  checked={checked}
                  onChange={handleChange}
                  sx={{
                    '&.Mui-checked': {
                      color: '#282828', // チェック時の色を指定
                    },
                  }}
                />
                <Typography
                  sx={{
                    font: 'normal normal normal 16px/24px Noto Sans JP',
                    textAlign: 'left',
                    letterSpacing: '0px',
                    color: '#282828',
                  }}
                >
                  {translation['label_skip_input']}
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              {loading === loadStatus.LOADING && <CircularProgress />}
            </Box>
            <Box sx={{ marginTop: '55px' }}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{
                  width: '205px',
                  height: '49px',
                  borderRadius: '10px',
                  boxShadow: 0,
                  textAlign: 'center',
                  font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                  letterSpacing: '0px',
                  color: '#FFFFFF',
                  '&:hover': {
                    background: '#282828 0% 0% no-repeat padding-box',
                    opacity: 0.8,
                    boxShadow: 0,
                  },
                }}
                disabled={isDisabled}
                style={{
                  backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
                }}
                onClick={onClickLogin}
              >
                {translation['button_login']}
              </Button>
            </Box>
            <Box mt={3}>{kyoceraJsx}</Box>
            <Box display="flex" justifyContent="center" alignItems="center" mt="24px">
              <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
                <Typography
                  mb="2px"
                  sx={{
                    font: 'normal normal normal 16px/24px Noto Sans JP',
                    textAlign: 'left',
                    letterSpacing: '0px',
                    color: '#282828',
                  }}
                >
                  {/* コピーライトが異なる場合は切替できるようにしておく(現状同じ) */}
                  {company === 'TA' ? translation['copyright_info'] : translation['copyright_info']}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          font: 'normal normal normal 12px/12px Noto Sans JP',
          color: '#999999',
        }}
      >
        {version ? `${translation['app_version']}`.replace('{version}', version) : ''}
      </Box>
    </>
  )
}
